import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import {
  useActiveCamera,
  useDefaultCamera,
} from "@/components/r3f/cameras/active-camera-context";
import { PointCloudRenderer } from "@/components/r3f/renderers/pointcloud-renderer";
import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { useAnimateCameraToPredefinedViews } from "@/hooks/use-animate-camera-to-predefined-views";
import { useCenterCameraOnPointCloud } from "@/hooks/use-center-camera-on-pointcloud";
import { useManageProjectionSwitch } from "@/hooks/use-manage-projection-switch";
import { useCached3DObject } from "@/object-cache";
import {
  computePositionsBoundingBox,
  usePanosBoundingBox,
} from "@/modes/overview-mode/use-update-overview-camera";
import { selectDataSet360s } from "@/modes/mode-selectors";
import { selectClippingBox } from "@/store/clipping-box-selectors";
import { setClippingBox } from "@/store/clipping-box-slice";
import { selectIpxOrderMode } from "@/store/modes/ipx-order-mode-selectors";
import {
  setIpxPointCloud,
  setIpxPointCloudMetrics,
} from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveWizardStepIndex } from "@/store/wizard/wizard-selectors";
import { toVec4Tuple } from "@/tools/clip-scene-tool";
import {
  computeBoxHandlesFramingView,
  computeOverviewFramingView,
} from "@/tools/smart-clipping/smart-clipping";
import {
  SmartClipping,
  useSmartClipping,
} from "@/tools/smart-clipping/use-smart-clipping";
import {
  BoxControls,
  ExplorationControls,
  StoreFboPassObj,
  useTypedEvent,
} from "@faro-lotv/app-component-toolbox";
import {
  IElementGenericPointCloudStream,
  isIElementGenericDataSession,
  isIElementGenericPointCloudStream,
} from "@faro-lotv/ielement-types";
import { PointerRotates, WalkOrbitControls } from "@faro-lotv/lotv";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { isEqual } from "es-toolkit";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { Box3, Quaternion, Vector3 } from "three";
import { ExportModeRenderingPipeline } from "../export-mode/export-mode-rendering-pipeline";
import { useComputeValidExportModeScene } from "../export-mode/export-mode-state";
import { useIsExportableDataset } from "../export-mode/scans-export/use-prepare-scans-export-data";
import {
  BoxControlsViaRef,
  useManageClippingPlanesChange,
} from "../export-mode/use-manage-clip-planes-change";
import { IpxFloorsHighlight } from "./floors/ipx-floors-highlight";
import { useIpxFloorEstimate } from "./floors/use-ipx-floor-estimate";
import { IPX_MOCK_FALLBACK_TOTAL_BYTES } from "./ipx-config";
import { IPX_ORDER_STEPS, IpxOrderStepKey } from "./ipx-order-steps";
import { getElementFileSize } from "./ipx-size-estimate";
import { useIpxHiddenScans } from "./use-ipx-hidden-scans";
import { IpxScanSelectionReporter } from "./ipx-scan-selection-reporter";
import { recenterIpxViewEvent } from "./ipx-view-events";

const DEFINE_AREA_STEP_INDEX = IPX_ORDER_STEPS.findIndex(
  (step) => step.key === IpxOrderStepKey.defineArea,
);

// Stay in the wizard even if the current selection has no valid point cloud
function noFallback(): void {}

/**
 * @param box An axis aligned box
 * @returns the same box as a clipping box
 */
function toObb(box: Box3): OrientedBoundingBox {
  return {
    position: box.getCenter(new Vector3()).toArray(),
    size: box.getSize(new Vector3()).toArray(),
    rotation: toVec4Tuple(new Quaternion()),
  };
}

/** @returns the scene of the IPX ordering wizard: the point cloud and the region box */
export function IpxOrderModeScene(): JSX.Element | null {
  useDefaultCamera();
  const dispatch = useAppDispatch();
  const defaultPointCloud = useComputeValidExportModeScene(noFallback);
  const { pointCloudId } = useAppSelector(selectIpxOrderMode);
  const selected = useAppSelector(selectIElement(pointCloudId));

  useEffect(() => {
    if (!pointCloudId && defaultPointCloud) {
      dispatch(setIpxPointCloud(defaultPointCloud.id));
    }
  }, [defaultPointCloud, dispatch, pointCloudId]);

  const pointCloud =
    selected && isIElementGenericPointCloudStream(selected)
      ? selected
      : defaultPointCloud;
  if (!pointCloud) return null;

  return (
    <>
      <IpxScanSelectionReporter />
      <PointCloudRegionScene key={pointCloud.id} pointCloud={pointCloud} />
    </>
  );
}

type PointCloudRegionSceneProps = {
  /** The point cloud to model */
  pointCloud: IElementGenericPointCloudStream;
};

function PointCloudRegionScene({
  pointCloud,
}: PointCloudRegionSceneProps): JSX.Element {
  const dispatch = useAppDispatch();
  const stepIndex = useAppSelector(selectActiveWizardStepIndex);
  const isEditingRegion = stepIndex === DEFINE_AREA_STEP_INDEX;

  const pointCloudObject = useCached3DObject(pointCloud, "IpxOrderModeScene");
  const {
    box: modelBB,
    cloudTransform,
    target: cloudCenter,
  } = useCenterCameraOnPointCloud(pointCloudObject);

  const sentScans = useAppSelector((state) => state.ipxOrderMode.sentScans);
  const scanPositions = useMemo(
    () => sentScans.map((scan) => scan.position),
    [sentScans],
  );
  const isScanSelectionAvailable = useIsExportableDataset();
  // The default box is computed once, around the initial scan selection
  const isSelectionReady = useAppSelector(
    (state) =>
      !isScanSelectionAvailable ||
      state.ipxOrderMode.totalScanCount !== undefined,
  );

  const hiddenScans = useIpxHiddenScans(pointCloudObject);
  const floors = useIpxFloorEstimate(pointCloudObject, hiddenScans);

  const [target, setTarget] = useState<Vector3 | undefined>(cloudCenter);
  const [boxPosition, setBoxPosition] = useState(() =>
    modelBB.getCenter(new Vector3()),
  );
  const [boxScale, setBoxScale] = useState(() =>
    modelBB.getSize(new Vector3()),
  );
  const [boxRotation, setBoxRotation] = useState(() => new Quaternion());

  const clippingBox = useAppSelector(selectClippingBox);
  useEffect(() => {
    if (!clippingBox) return;
    setBoxPosition(new Vector3().fromArray(clippingBox.position));
    setBoxScale(new Vector3().fromArray(clippingBox.size));
    setBoxRotation(new Quaternion().fromArray(clippingBox.rotation));
  }, [clippingBox]);
  const isPreparingBox = isEditingRegion && !clippingBox;
  const isClippingBoxEnabled = useAppSelector(
    (state) => state.ipxOrderMode.isClippingBoxEnabled,
  );

  const storeBox = useCallback(
    (obb: OrientedBoundingBox | undefined) => dispatch(setClippingBox(obb)),
    [dispatch],
  );

  const [boxControls, setBoxControls] = useState<BoxControlsViaRef>(null);
  const [controlsEnabled, setControlsEnabled] = useState(true);

  const storeFboRef = useRef<StoreFboPassObj>(null);

  const onInteractionStopped = useCallback(() => {
    setControlsEnabled(true);
    if (!boxControls) return;
    storeBox({
      position: boxControls.pos.toArray(),
      size: boxControls.size.toArray(),
      rotation: toVec4Tuple(boxControls.rot),
    });
  }, [boxControls, storeBox]);

  const { onClippingPlanesChanged, clippingPlanesPreview } =
    useManageClippingPlanesChange(
      modelBB,
      cloudTransform.worldMatrix,
      boxControls,
    );

  const {
    cameraAnimation,
    setCameraAnimation,
    setControlsForResetView,
    clippingBox: activeBox,
    setCameraView,
  } = useAnimateCameraToPredefinedViews(modelBB, setTarget);

  // A box drag interrupted by an animation or a step change never reports its end, don't leave the navigation disabled
  useEffect(() => {
    if (!isEditingRegion || cameraAnimation) setControlsEnabled(true);
  }, [cameraAnimation, isEditingRegion]);

  const { setControls: setControlsForCameraSwitch } = useManageProjectionSwitch(
    activeBox,
    setCameraView,
  );

  const [controls, setControlsState] = useState<WalkOrbitControls | null>(null);
  const setControls = useCallback(
    (controls: WalkOrbitControls | null) => {
      setControlsForCameraSwitch(controls);
      setControlsForResetView(controls);
      setControlsState(controls);
    },
    [setControlsForCameraSwitch, setControlsForResetView],
  );

  // Once the user takes over the camera, the default box computation stops moving it
  const [hasNavigated, setHasNavigated] = useState(false);
  useEffect(() => {
    if (!controls) return;
    function onUserInteracted(): void {
      setHasNavigated(true);
    }
    controls.userInteracted.on(onUserInteracted);
    return () => controls.userInteracted.off(onUserInteracted);
  }, [controls]);

  const { predefinedView } = useActiveCamera();
  useEffect(() => {
    if (predefinedView) setHasNavigated(true);
  }, [predefinedView]);

  // The scan positions show where the building is, the point cloud bounds include stray points
  const panos = useAppSelector((state) => {
    const session = selectAncestor(
      pointCloud,
      isIElementGenericDataSession,
    )(state);
    return session ? selectDataSet360s(session, false, false)(state) : [];
  }, isEqual);
  const panosBox = usePanosBoundingBox(panos, undefined);
  const framingView = useMemo(() => {
    const selectedScansBox = computePositionsBoundingBox(
      scanPositions.map((position) => new Vector3().fromArray(position)),
    );
    return computeOverviewFramingView(selectedScansBox ?? panosBox ?? modelBB);
  }, [modelBB, panosBox, scanPositions]);
  // The camera stays where it is when the selection changes, as in the SWEB-9013 prototype:
  // framing each selection zoomed in too much on small clusters

  const [defaultRegion, setDefaultRegion] = useState<SmartClipping>();
  const applyDefaultBox = useCallback(
    (box: Box3) => {
      storeBox(toObb(box));
      dispatch(
        setIpxPointCloudMetrics({
          size: box.getSize(new Vector3()).toArray(),
          totalBytes:
            getElementFileSize(pointCloud) ?? IPX_MOCK_FALLBACK_TOTAL_BYTES,
        }),
      );
    },
    [dispatch, pointCloud, storeBox],
  );
  // The whole point cloud with the handles of a box around it in view, so the user starts with an overview
  const aspect = useThree((state) => state.size.width / state.size.height);
  const computeWholeCloudView = useCallback(
    (rotation: Quaternion) =>
      computeBoxHandlesFramingView(modelBB, rotation, aspect),
    [aspect, modelBB],
  );
  const onDefaultRegionComputed = useCallback(
    (region: SmartClipping) => {
      setDefaultRegion(region);
      applyDefaultBox(region.box);
      setCameraView(computeWholeCloudView(region.view.rotation));
    },
    [applyDefaultBox, computeWholeCloudView, setCameraView],
  );
  // The user takes over the camera before the default box is ready: stop moving it and box the whole data
  useEffect(() => {
    if (isPreparingBox && isSelectionReady && hasNavigated) {
      applyDefaultBox(modelBB);
    }
  }, [
    applyDefaultBox,
    hasNavigated,
    isPreparingBox,
    isSelectionReady,
    modelBB,
  ]);
  useSmartClipping({
    storeFboRef,
    pointCloud: pointCloudObject,
    scannerPositions: scanPositions,
    startView: framingView,
    fallbackBox: modelBB,
    isActive: isPreparingBox && isSelectionReady && !hasNavigated,
    isCameraAnimating: !!cameraAnimation,
    setCameraView,
    onComputed: onDefaultRegionComputed,
  });
  const resetBox = useCallback(() => {
    // Clearing the box computes it again around the scans selected now
    setHasNavigated(false);
    storeBox(undefined);
  }, [storeBox]);

  useTypedEvent(
    recenterIpxViewEvent,
    useCallback(
      () =>
        setCameraView(
          defaultRegion
            ? computeWholeCloudView(defaultRegion.view.rotation)
            : framingView,
        ),
      [computeWholeCloudView, defaultRegion, framingView, setCameraView],
    ),
  );

  const interactiveObjects = useMemo(
    () => [pointCloudObject],
    [pointCloudObject],
  );

  return (
    <>
      <PointCloudRenderer
        pointCloud={pointCloudObject}
        clippingPlanes={
          clippingBox && isClippingBoxEnabled
            ? clippingPlanesPreview
            : undefined
        }
        hiddenScans={hiddenScans}
      />
      <IpxFloorsHighlight
        pointCloud={pointCloudObject}
        segmentation={floors}
        visible={isEditingRegion}
      />
      <BoxControls
        ref={setBoxControls}
        position={boxPosition}
        size={boxScale}
        rotation={boxRotation}
        clipInside={false}
        enableRotateX={false}
        enableRotateY={false}
        isVisible={isEditingRegion && !!clippingBox && isClippingBoxEnabled}
        enabled={isEditingRegion && isClippingBoxEnabled && !cameraAnimation}
        onInteractionStarted={() => setControlsEnabled(false)}
        onInteractionStopped={onInteractionStopped}
        clippingPlanesChanged={onClippingPlanesChanged}
      />
      {!cameraAnimation && (
        <ExplorationControls
          ref={setControls}
          target={target}
          obstacles={[]}
          clickableModels={interactiveObjects}
          enableClickToFocus={controlsEnabled}
          enableDoubleClickToZoom={controlsEnabled}
          pointerRotates={PointerRotates.ViewDirection}
          enabled={controlsEnabled}
        />
      )}
      {cameraAnimation && (
        <CameraAnimation
          {...cameraAnimation}
          // The intermediate views used to compute the default box are not meant to be watched
          duration={isPreparingBox ? 0 : undefined}
          onAnimationFinished={() => setCameraAnimation(undefined)}
        />
      )}
      <ExportModeRenderingPipeline
        pointCloudObject={pointCloudObject}
        onAutoClipBox={storeBox}
        onResetClipBox={resetBox}
        storeFboRef={storeFboRef}
      />
    </>
  );
}
